import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useDemo } from "@/store/DemoStore";
import { api, errMsg } from "@/lib/api";
import { Avatar, Card, CardTitle, PageHeader, avatarUrl, btn, normalizePhone, isPhoneKey, normalizePhoneOnBlur } from "@/components/common";
import { PushMasterToggle } from "@/components/NotificationBell";
import { cn } from "@/lib/utils";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

function Form({ title, fields, values, onSave, testId, extra }) {
  const [v, setV] = useState(values);
  return (
    <Card data-testid={testId}>
      <CardTitle>{title}</CardTitle>
      <form className="grid gap-4 p-5 sm:grid-cols-2 sm:p-6" onSubmit={(e) => { e.preventDefault(); onSave(v); }}>
        {extra}
        {fields.map(([k, label, type = "text"]) => (
          <label key={k}>
            <span className="mb-1.5 block text-xs font-medium text-slate-500">{label}</span>
            <input data-testid={`settings-${k}-input`} type={type} className={field} value={v[k]} onChange={(e) => setV({ ...v, [k]: type === "number" ? +e.target.value : isPhoneKey(k) ? normalizePhone(e.target.value) : e.target.value })} onBlur={isPhoneKey(k) ? normalizePhoneOnBlur((n) => setV((p) => ({ ...p, [k]: n }))) : undefined} />
          </label>
        ))}
        <div className="sm:col-span-2"><button type="submit" className={btn.primary} data-testid={`${testId}-save`}>Save Changes</button></div>
      </form>
    </Card>
  );
}

function AvatarUpload({ settings }) {
  const { refresh } = useDemo();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = avatarUrl(settings.advisorId, settings.advisorAvatar);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) return toast.error("Please choose a PNG, JPG or WEBP image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be 5MB or smaller");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/avatar", fd);
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete("/settings/avatar"); await refresh(); toast.success("Profile photo removed"); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-4 sm:col-span-2" data-testid="avatar-upload">
      <Avatar name={settings.advisorName} src={src} size="lg" />
      <div className="min-w-0">
        <p className="font-semibold text-navy-900">{settings.advisorName}</p>
        <p className="text-sm text-slate-500">Shown to clients in MyTrip. PNG, JPG or WEBP, up to 5MB.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onPick} data-testid="avatar-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={cn(btn.secondary, "h-9")} data-testid="avatar-upload-btn">{busy ? "Uploading…" : src ? "Change Photo" : "Upload Photo"}</button>
          {src && <button type="button" onClick={remove} disabled={busy} className={cn(btn.ghost, "h-9 text-red-600 hover:bg-red-50")} data-testid="avatar-remove-btn">Remove</button>}
        </div>
      </div>
    </div>
  );
}

function PasswordCard() {
  const [f, setF] = useState({ current: "", new: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new !== f.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current: f.current, new: f.new });
      toast.success("Password updated");
      setF({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <Card data-testid="password-settings">
      <CardTitle>Change Password</CardTitle>
      <form onSubmit={submit} className="grid gap-4 p-5 sm:grid-cols-3 sm:p-6">
        {[["current", "Current password"], ["new", "New password (8+ characters)"], ["confirm", "Confirm new password"]].map(([k, l]) => (
          <label key={k}>
            <span className="mb-1.5 block text-xs font-medium text-slate-500">{l}</span>
            <input type="password" required minLength={k === "current" ? 1 : 8} autoComplete={k === "current" ? "current-password" : "new-password"} className={field} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-password-${k}`} />
          </label>
        ))}
        <div className="sm:col-span-3"><button disabled={busy} className={btn.primary} data-testid="settings-password-submit">Update Password</button></div>
      </form>
      <div className="flex items-center justify-center gap-4 border-t border-slate-100 px-5 py-4 text-xs">
        <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="settings-privacy-link" className="font-medium text-slate-500 transition-colors hover:text-navy-900">Privacy Policy</Link>
        <span className="h-3 w-px bg-slate-200" />
        <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="settings-terms-link" className="font-medium text-slate-500 transition-colors hover:text-navy-900">Terms &amp; Conditions</Link>
      </div>
    </Card>
  );
}

export default function SettingsPage() {
  const { settings, updateSettings, isAdmin } = useDemo();
  const save = async (patch) => { if (await updateSettings(patch)) toast.success("Settings saved"); };
  const pick = (keys) => Object.fromEntries(keys.map((k) => [k, settings[k]]));

  return (
    <div className="max-w-4xl">
      <PageHeader title="Settings" subtitle="Your advisor profile and agency preferences." />
      <div className="space-y-6">
        <Form testId="profile-settings" title="Advisor Profile" values={pick(["advisorName", "advisorTitle", "advisorEmail", "advisorPhone"])} onSave={save}
          extra={<AvatarUpload settings={settings} />}
          fields={[["advisorName", "Full name"], ["advisorTitle", "Title"], ["advisorEmail", "Email", "email"], ["advisorPhone", "Phone"]]} />
        {isAdmin && <Form testId="agency-settings" title="Agency (admin only)" values={pick(["agencyName", "agencyWebsite", "agencyPhone"])} onSave={save}
          fields={[["agencyName", "Agency name"], ["agencyWebsite", "Website"], ["agencyPhone", "Main phone"]]} />}
        <Card data-testid="notification-settings">
          <CardTitle>Notifications</CardTitle>
          <ul className="divide-y divide-slate-100 px-5 py-2 sm:px-6">
            <li><PushMasterToggle enabled={!!settings.pushEnabled} /></li>
            {[["notifyMessages", "New Client Messages"], ["notifyPayments", "Upcoming Payment Reminders"], ["notifyDepartures", "Departure Countdown Digest"]].map(([k, l]) => (
              <li key={k} className="flex items-center justify-between gap-4 py-3.5">
                <span className="text-sm text-navy-900">{l}</span>
                <Switch data-testid={`settings-${k}-switch`} checked={settings[k]} onCheckedChange={(c) => updateSettings({ [k]: c })} />
              </li>
            ))}
          </ul>
        </Card>
        <PasswordCard />
        {isAdmin && (
          <Card className="lg:hidden" data-testid="mobile-admin-links">
            <CardTitle>Admin</CardTitle>
            <div className="grid grid-cols-2 gap-2 p-5">
              {[["users", "Users"], ["agents", "Agents"], ["communication", "Communication"], ["marketing", "Marketing"]].map(([k, l]) => <Link key={k} to={`/agent/${k}`} className={btn.secondary} data-testid={`mobile-admin-${k}`}>{l}</Link>)}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
